import { inject, Service } from '@angular/core';
import { UpsertOwnerCorporateDataDtoInput } from '@h5s/dtos-api';
import {
  CheckLoginElectronicInvoiceGql,
  CheckLoginElectronicInvoiceQueryResult,
  CheckLoginElectronicInvoiceQueryVariables,
  CreateOwnerCorporateData,
  CreateOwnerCorporateDataGql,
  CreateOwnerCorporateDataMutationResult,
  CreateOwnerCorporateDataMutationVariables,
  DeleteOwnerCorporateDataGql,
  DeleteOwnerCorporateDataMutationResult,
  DeleteOwnerCorporateDataMutationVariables,
  GetAllOwnerCorporateDataGql,
  GetAllOwnerCorporateDataListGql,
  GetAllOwnerCorporateDataListQueryResult,
  GetAllOwnerCorporateDataQueryResult,
  GetOwnerCorporateDataGql,
  GetOwnerCorporateDataQueryResult,
  GetOwnerCorporateDataQueryVariables,
  OwnerCorporateData,
  OwnerCorporateDataList,
  ResolveOwnerCorporateDataGql,
  ResolveOwnerCorporateDataQueryResult,
  UpdateOwnerCorporateData,
  UpdateOwnerCorporateDataGql,
  UpdateOwnerCorporateDataMutationResult,
  UpdateOwnerCorporateDataMutationVariables,
} from '@h5s/entities';
import { getValueOrError, throwErrorIfNullFromGraphQL } from '@h5s/util-error';
import { mapArrayResponse, mapParamDto, mapResponse } from '@h5s/util-object';
import { Apollo } from 'apollo-angular';
import { map, Observable } from 'rxjs';

@Service()
export class DataAccessCorporateDataService {
  private readonly apollo = inject(Apollo);
  /**
   * Carica le anagrafiche fiscali
   */
  public getAllOwnerCorporateDataList(): Observable<OwnerCorporateDataList[]> {
    return this.apollo
      .query<GetAllOwnerCorporateDataListQueryResult>({
        query: GetAllOwnerCorporateDataListGql,
      })
      .pipe(
        throwErrorIfNullFromGraphQL((data) => data),
        map((result) => mapArrayResponse(OwnerCorporateDataList, result.getAllOwnerCorporateData)),
      );
  }

  /**
   * Ottiene la lista di configurazioni delle anagrafiche di fatturazione
   *
   * @returns {Observable<OwnerCorporateData[]>} Un observable che emette l'oggetto contenente la lista di configurazioni delle anagrafiche di fatturazione
   */
  public getAllOwnerCorporateData(): Observable<OwnerCorporateData[]> {
    return this.apollo
      .query<GetAllOwnerCorporateDataQueryResult>({
        query: GetAllOwnerCorporateDataGql,
      })
      .pipe(
        throwErrorIfNullFromGraphQL((data) => data, '[DataAccessOwnerService] OwnerCorporateData not found'),
        map((owner) => mapArrayResponse(OwnerCorporateData, owner.getAllOwnerCorporateData)),
      );
  }

  /**
   * Carica un anagrafica fiscale specifica per id ownerCorporateData
   */
  public getCorporateData(ownerCorporateDataId: number): Observable<OwnerCorporateData> {
    const variables: GetOwnerCorporateDataQueryVariables = {
      id: ownerCorporateDataId,
    };

    return this.apollo
      .query<GetOwnerCorporateDataQueryResult>({
        query: GetOwnerCorporateDataGql,
        variables: variables,
      })
      .pipe(
        throwErrorIfNullFromGraphQL((data) => data),
        map((data) => mapResponse(OwnerCorporateData, data.getOwnerCorporateData)),
      );
  }

  /**
   * Crea un'anagrafica fiscale
   * @param createCorporateData
   * @returns {Observable<number>}
   */
  public createOwnerCorporateData(createOwnerCorporateData: CreateOwnerCorporateData): Observable<number> {
    const upsertOwnerCorporateDataDTO = mapParamDto<UpsertOwnerCorporateDataDtoInput>(createOwnerCorporateData);

    const variables: CreateOwnerCorporateDataMutationVariables = {
      upsertOwnerCorporateDataDTO,
    };

    return this.apollo
      .mutate<CreateOwnerCorporateDataMutationResult>({
        mutation: CreateOwnerCorporateDataGql,
        variables: variables,
      })
      .pipe(
        throwErrorIfNullFromGraphQL((data) => data),
        map((result) => getValueOrError(result.createOwnerCorporateData)),
      );
  }

  /**
   * Aggiorna parzialmente la corporateData
   */
  public updateOwnerCorporateData(updateOwnerCorporateData: UpdateOwnerCorporateData): Observable<boolean> {
    const upsertOwnerCorporateDataDTO = mapParamDto<UpsertOwnerCorporateDataDtoInput>(updateOwnerCorporateData);

    const variables: UpdateOwnerCorporateDataMutationVariables = {
      upsertOwnerCorporateDataDTO,
    };

    return this.apollo
      .mutate<UpdateOwnerCorporateDataMutationResult>({
        mutation: UpdateOwnerCorporateDataGql,
        variables: variables,
      })
      .pipe(
        throwErrorIfNullFromGraphQL((data) => data),
        map((result) => result.updateOwnerCorporateData),
      );
  }

  /**
   * Elimina l'anagrafica fiscale
   * @param {number} corporateDataId
   */
  public deleteOwnerCorporateData(corporateDataId: number): Observable<boolean> {
    const variables: DeleteOwnerCorporateDataMutationVariables = {
      id: corporateDataId,
    };

    return this.apollo
      .mutate<DeleteOwnerCorporateDataMutationResult>({
        mutation: DeleteOwnerCorporateDataGql,
        variables: variables,
      })
      .pipe(
        throwErrorIfNullFromGraphQL((data) => data),
        map((result) => result.deleteOwnerCorporateData),
      );
  }

  /**
   * Controlla se le credenziali per la fatturazione elettronica sono corrette
   * @param corporateData
   * @returns
   */
  public checkLoginElectronicInvoice(corporateData: UpdateOwnerCorporateData): Observable<boolean> {
    const upsertOwnerCorporateDataDTO = mapParamDto<UpsertOwnerCorporateDataDtoInput>(corporateData);

    const variables: CheckLoginElectronicInvoiceQueryVariables = {
      upsertOwnerCorporateDataDTO,
    };

    return this.apollo
      .query<CheckLoginElectronicInvoiceQueryResult>({
        query: CheckLoginElectronicInvoiceGql,
        variables: variables,
      })
      .pipe(
        throwErrorIfNullFromGraphQL((data) => data),
        map((result) => result.checkLoginElectronicInvoice),
      );
  }

  /**
   * Recupera i dati corporate dell'owner a partire dal block
   *
   * @param {string | null} block
   * @returns {Observable<OwnerCorporateData>}
   */
  public resolveOwnerCorporateData(block: string | null): Observable<OwnerCorporateData> {
    return this.apollo
      .query<ResolveOwnerCorporateDataQueryResult>({
        query: ResolveOwnerCorporateDataGql,
        variables: { request: { block } },
      })
      .pipe(
        throwErrorIfNullFromGraphQL((data) => data),
        map((data) => mapResponse(OwnerCorporateData, data.resolveOwnerCorporateData)),
      );
  }
}
